export interface SurnameCategory {
  id: number;
  name: string;
  isValidated?: boolean;
}

export interface GotraSurname {
  id: number;
  name: string;
  order?: number;
  isValidated?: boolean;
  categories: SurnameCategory[];
}

export interface UpdateSurnamePayload {
  name?: string;
  order?: number;
}

export interface Pravara {
  id: number;
  name: string;
  order?: number;
  groupIds?: number[];
  isValidated?: boolean;
}

export interface PravaraInGroup {
  id: number;
  name: string;
  order?: number;
  isValidated?: boolean;
}

export interface PravaraGroup {
  id: number;
  order?: number;
  isValidated?: boolean;
  pravaras: PravaraInGroup[];
}

export interface UpdatePravaraPayload {
  name?: string;
  order?: number;
  groupIds?: number[];
}

export interface CreatePravaraGroupPayload {
  order?: number;
  pravaraIds: number[];
}

export interface UpdatePravaraGroupPayload {
  order?: number;
  pravaraIds?: number[];
}

export interface AddPravaraPayload {
  name: string;
  order?: number;
  groupIds?: number[];
}

export interface OrderableItem {
  id: number;
  order?: number;
}

export const sortByOrder = <T extends OrderableItem>(items: T[]): T[] =>
  items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const orderDiff = (a.item.order ?? 0) - (b.item.order ?? 0);
      if (orderDiff !== 0) return orderDiff;
      return a.index - b.index;
    })
    .map(({ item }) => item);

export const sortSurnamesByOrder = (surnames: GotraSurname[]): GotraSurname[] =>
  sortByOrder(surnames);

export const sortPravarasByOrder = (pravaras: Pravara[]): Pravara[] => sortByOrder(pravaras);

export const sortPravaraGroupsByOrder = (groups: PravaraGroup[]): PravaraGroup[] =>
  sortByOrder(groups);

export const getPravaraGroupIds = (pravara: Pick<Pravara, 'groupIds'>): number[] =>
  [...(pravara.groupIds ?? [])];

export const isUngroupedPravara = (pravara: Pravara): boolean =>
  getPravaraGroupIds(pravara).length === 0;

export const getUngroupedPravaras = (pravaras: Pravara[]): Pravara[] =>
  sortPravarasByOrder(pravaras.filter(isUngroupedPravara));

export const toPravaraInGroup = (pravara: Pravara): PravaraInGroup => ({
  id: pravara.id,
  name: pravara.name,
  order: pravara.order,
  isValidated: pravara.isValidated,
});

export const buildPravaraGroupWithMembers = (
  gotra: Gotra,
  group: PravaraGroup,
  memberPravaraIds: number[],
): PravaraGroup => {
  const pravaras =
    group.pravaras?.length
      ? group.pravaras
      : sortPravarasByOrder(
          gotra.pravara.filter((pravara) => memberPravaraIds.includes(pravara.id)),
        ).map(toPravaraInGroup);
  return { ...group, pravaras };
};

export const enrichGotraPravaraGroups = (gotra: Gotra): Gotra => {
  const flatPravara = gotra.pravara || [];
  const groups = gotra.pravaraGroups || [];
  if (!groups.length) return gotra;

  const enrichedGroups = sortPravaraGroupsByOrder(
    groups
      .map((group) => {
        const nested = group.pravaras || [];
        const pravaras =
          nested.length > 0
            ? sortByOrder(nested)
            : sortByOrder(
                flatPravara
                  .filter((pravara) => getPravaraGroupIds(pravara).includes(group.id))
                  .map(toPravaraInGroup),
              );
        return { ...group, pravaras };
      })
      .filter((group) => group.pravaras.length > 0),
  );

  return { ...gotra, pravaraGroups: enrichedGroups };
};

export const getOrderUpdatesAfterMove = (
  items: OrderableItem[],
  itemId: number,
  direction: -1 | 1,
): Array<{ id: number; order: number }> | null => {
  const sorted = sortByOrder(items);
  const index = sorted.findIndex((item) => item.id === itemId);
  const targetIndex = index + direction;
  if (index < 0 || targetIndex < 0 || targetIndex >= sorted.length) return null;

  const orderCounts = new Map<number, number>();
  for (const item of items) {
    const current = item.order ?? 0;
    orderCounts.set(current, (orderCounts.get(current) || 0) + 1);
  }

  const reordered = [...sorted];
  const [moved] = reordered.splice(index, 1);
  reordered.splice(targetIndex, 0, moved);

  return reordered
    .map((item, order) => ({
      id: item.id,
      order,
      currentOrder: item.order ?? 0,
    }))
    .filter(
      ({ currentOrder, order }) =>
        currentOrder !== order || (orderCounts.get(currentOrder) || 0) > 1,
    )
    .map(({ id, order }) => ({ id, order }));
};

export interface Gotra {
  id: number;
  name: string;
  isValidated?: boolean;
  pravara: Pravara[];
  pravaraGroups?: PravaraGroup[];
  surnames: GotraSurname[];
}

export const normalizeGotra = (gotra: Gotra): Gotra => {
  const dedupedPravaraGroups = Array.from(
    new Map((gotra.pravaraGroups || []).map((group) => [group.id, group])).values(),
  );

  return enrichGotraPravaraGroups({
    ...gotra,
    isValidated: Boolean(gotra.isValidated),
    pravara: (gotra.pravara || []).map((pravara) => ({
      ...pravara,
      groupIds: getPravaraGroupIds(pravara),
      isValidated: Boolean(pravara.isValidated),
    })),
    pravaraGroups: dedupedPravaraGroups.map((group) => ({
      ...group,
      isValidated: Boolean(group.isValidated),
      pravaras: (group.pravaras || []).map((pravara) => ({
        ...pravara,
        isValidated: Boolean(pravara.isValidated),
      })),
    })),
    surnames: (gotra.surnames || []).map((surname) => ({
      ...surname,
      isValidated: Boolean(surname.isValidated),
      categories: (surname.categories || []).map((category) => ({
        ...category,
        isValidated: Boolean(category.isValidated),
      })),
    })),
  });
};

export const mergeCreatedPravaraGroup = (gotra: Gotra, newGroup: PravaraGroup): Gotra => {
  const alreadyListed = (gotra.pravaraGroups || []).some((group) => group.id === newGroup.id);
  const pravaraGroups = alreadyListed
    ? gotra.pravaraGroups || []
    : sortPravaraGroupsByOrder([...(gotra.pravaraGroups || []), newGroup]);
  const memberIds = new Set((newGroup.pravaras || []).map((pravara) => pravara.id));
  const pravara = gotra.pravara.map((item) => {
    if (!memberIds.has(item.id)) return item;
    const groupIds = getPravaraGroupIds(item);
    if (groupIds.includes(newGroup.id)) return item;
    return { ...item, groupIds: [...groupIds, newGroup.id] };
  });
  return { ...gotra, pravaraGroups, pravara };
};

export const mergeRefreshedGotraWithCreatedGroup = (
  refreshed: Gotra,
  newGroup: PravaraGroup,
): Gotra => {
  const normalized = normalizeGotra(refreshed);
  if (normalized.pravaraGroups?.some((group) => group.id === newGroup.id)) {
    return normalized;
  }
  return normalizeGotra(mergeCreatedPravaraGroup(normalized, newGroup));
};

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export const isGotraResponse = (data: unknown): data is Gotra =>
  typeof data === 'object' &&
  data !== null &&
  'pravara' in data &&
  Array.isArray((data as Gotra).pravara) &&
  'surnames' in data;

export const findPravaraGroupContainingAll = (
  groups: PravaraGroup[] | undefined,
  pravaraIds: number[],
): PravaraGroup | undefined => {
  if (!pravaraIds.length) return undefined;
  const required = new Set(pravaraIds);
  return (groups || []).find((group) => {
    const memberIds = new Set((group.pravaras || []).map((pravara) => pravara.id));
    return Array.from(required).every((id) => memberIds.has(id));
  });
};

/** POST pravara-group may return the full gotra or a single group. */
export const resolveGotraAfterPravaraGroupCreate = async (
  gotraId: number,
  responseData: Gotra | PravaraGroup,
  context: { selectedGotra: Gotra; memberPravaraIds: number[] },
  fetchGotra: (id: number) => Promise<Gotra>,
): Promise<Gotra> => {
  if (isGotraResponse(responseData)) {
    return normalizeGotra(responseData);
  }
  const createdGroup = buildPravaraGroupWithMembers(
    context.selectedGotra,
    responseData,
    context.memberPravaraIds,
  );
  return resolveGotraAfterCreatePravaraGroup(gotraId, createdGroup, fetchGotra);
};

/** After POST pravara-group: prefer server refresh; never merge into stale local gotra. */
export const resolveGotraAfterCreatePravaraGroup = async (
  gotraId: number,
  createdGroup: PravaraGroup,
  fetchGotra: (id: number) => Promise<Gotra>,
): Promise<Gotra> => {
  const read = async () => normalizeGotra(await fetchGotra(gotraId));
  let refreshed = await read();
  if (!refreshed.pravaraGroups?.some((group) => group.id === createdGroup.id)) {
    await delay(250);
    refreshed = await read();
  }
  if (refreshed.pravaraGroups?.some((group) => group.id === createdGroup.id)) {
    return refreshed;
  }
  return mergeRefreshedGotraWithCreatedGroup(refreshed, createdGroup);
};

export const filterGotraByValidation = (
  gotra: Gotra,
  options: { showValidated: boolean; showUnvalidated: boolean },
): Gotra | null => {
  const { showValidated, showUnvalidated } = options;
  if (!showValidated && !showUnvalidated) return null;

  const matchesValidation = (isValidated?: boolean) => {
    const validated = Boolean(isValidated);
    return validated ? showValidated : showUnvalidated;
  };

  const filteredPravaraGroups = (gotra.pravaraGroups || [])
    .filter((group) => matchesValidation(group.isValidated))
    .map((group) => ({
      ...group,
      pravaras: (group.pravaras || []).filter((pravara) =>
        matchesValidation(pravara.isValidated),
      ),
    }));

  const filteredGroupIds = new Set(filteredPravaraGroups.map((group) => group.id));
  const filteredPravara = (gotra.pravara || []).filter((pravara) => {
    if (!matchesValidation(pravara.isValidated)) return false;
    const groupIds = getPravaraGroupIds(pravara);
    if (!groupIds.length) return true;
    return groupIds.some((groupId) => filteredGroupIds.has(groupId));
  });

  const filteredSurnames = (gotra.surnames || [])
    .filter((surname) => matchesValidation(surname.isValidated))
    .map((surname) => ({
      ...surname,
      categories: (surname.categories || []).filter((category) =>
        matchesValidation(category.isValidated),
      ),
    }));

  const gotraMatches = matchesValidation(gotra.isValidated);
  const hasMatchingPravara =
    filteredPravaraGroups.length > 0 || filteredPravara.length > 0;
  if (!gotraMatches && filteredSurnames.length === 0 && !hasMatchingPravara) return null;

  return {
    ...gotra,
    pravara: filteredPravara,
    pravaraGroups: filteredPravaraGroups,
    surnames: filteredSurnames,
  };
};

export const parseNameList = (text: string) =>
  Array.from(new Set(text.split(/[\n,]+/).map((part) => part.trim()).filter(Boolean)));

export const formatValidationOptionLabel = (name: string, isValidated?: boolean) =>
  `${name} (${isValidated ? 'verified' : 'unverified'})`;

export interface CreateGotraPayload {
  name: string;
  pravara: string[];
  surnames?: Array<{
    name: string;
    categories?: Array<{ name: string }>;
  }>;
}

export interface UpdateGotraPayload {
  name?: string;
  pravara?: string[];
  surnames?: Array<{
    name: string;
    categories?: Array<{ name: string }>;
  }>;
}

export interface GotraSeedResult {
  message: string;
  sourceRecords: number;
  uniqueGotras: number;
  mergedDuplicateRows: number;
  created: number;
  updated: number;
  jsonPath: string;
}

export interface GotraSuggestion {
  id: number;
  name: string;
  phone: string;
  text: string;
  createdAt: string;
}

export interface CreateGotraSuggestionPayload {
  name: string;
  phone: string;
  text: string;
}
